import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { BaseService } from './base.service';
import { DataStorageService } from './storage';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private redirectUrl: string | null = null;
  private currentUser = signal<any>(null);

  setRedirectUrl(url: string | null): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl;
  }

  private refreshInProgress$?: Observable<string>;

  get canRefresh(): boolean {
    return this.refreshInProgress$ == null;
  }

  get authToken(): string | undefined {
    return this.storageService.getAccessToken();
  }

  get isAuthenticated(): boolean {
    return !!this.authToken;
  }

  get authenticated$(): Observable<string | undefined> {
    return this.storageService.onAccessTokenChanged();
  }

  private httpService = inject(BaseService);
  private storageService = inject(DataStorageService);

  getCurrentUser(): Observable<any> {
    if (this.currentUser()) {
      return of(this.currentUser());
    }
    return this.httpService.get<any>('auth/profile').pipe(
      map((response: any) => {
        this.currentUser.set(response.user);
        return response.user;
      }),
    );
  }

  login(authData: any): Observable<boolean> {
    return this.httpService.post<any>('auth/login-without-otp', authData).pipe(
      map((response: any) => {
        const { accessToken, refreshToken } = response;
        this.storageService.setTokens(accessToken, refreshToken);
        return !!accessToken && !!refreshToken;
      }),
    );
  }

  refreshToken(errorMessage?: string): Observable<string> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refreshToken = this.storageService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error(errorMessage));
    }

    this.refreshInProgress$ = this.httpService
      .post<any>('auth/refresh', {
        refresh_token: refreshToken,
      })
      .pipe(
        map((response: any) => {
          this.storageService.setTokens(
            response?.accessToken?.value,
            response?.refreshToken?.value,
          );
          return response?.accessToken?.value;
        }),
        catchError(err => {
          this.logout();
          return throwError(() => err);
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInProgress$ = undefined;
        }),
      );

    return this.refreshInProgress$;
  }

  logout(): void {
    this.storageService.clearAll();
    this.currentUser.set(null);
  }
}
